import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import {
  CalendarDaysIcon,
  PencilIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import {
  fetchWorkOrderProjectEvents,
  pickPrimaryPlanForWorkOrder,
} from "../../../api/projectPlan";
import PlanScheduleModal from "./plan-schedule-modal/PlanScheduleModal";
import { ProjectEventDialog } from "../projects/projectEventDialog";
import { stripHtml } from "../../Utils";

const PLAN_AVATAR_COLORS = [
  "#0E8C7B",
  "#2563EB",
  "#C2710C",
  "#7A5AF0",
  "#15924E",
  "#D6443A",
  "#0891B2",
  "#EC4899",
];

const planAvatar = (user, idx) => {
  const name = user?.displayTitle || user?.name || "?";
  const init = name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const color =
    PLAN_AVATAR_COLORS[(Number(user?.id) || idx) % PLAN_AVATAR_COLORS.length];
  return { init, color, name };
};

const uniquePlanUsers = (resources = []) => {
  const out = [];
  const seen = new Set();
  resources.forEach((r) => {
    const u = r?.user;
    if (u?.id && !seen.has(u.id)) {
      seen.add(u.id);
      out.push(u);
    }
  });
  return out;
};

const formatResourceWhen = (resource) => {
  if (!resource?.start_time) return null;
  const start = new Date(resource.start_time);
  const end = resource.end_time ? new Date(resource.end_time) : null;
  if (Number.isNaN(start.getTime())) return null;
  const datePart = format(start, "d. MMM yyyy", { locale: currentDateFnsLocale() });
  const timeStart = format(start, "HH:mm", { locale: currentDateFnsLocale() });
  if (end && !Number.isNaN(end.getTime())) {
    return `${datePart} kl. ${timeStart}–${format(end, "HH:mm", { locale: currentDateFnsLocale() })}`;
  }
  return `${datePart} kl. ${timeStart}`;
};

export const WorkOrderPlanSection = ({
  workOrder,
  project,
  canEdit = false,
  onPlansMutated,
}) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [createMode, setCreateMode] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventForDialog, setEventForDialog] = useState(null);

  const projectId = workOrder?.project_id ?? project?.id;
  const resolvedProject = useMemo(
    () => project ?? workOrder?.project ?? (projectId ? { id: projectId } : null),
    [project, workOrder?.project, projectId]
  );

  const loadPlans = useCallback(async () => {
    if (!workOrder?.id) return;
    setLoading(true);
    try {
      const res = await fetchWorkOrderProjectEvents(workOrder.id, {
        fields:
          "id,title,description,status,start_date,end_date,project_id,work_order_id,project_activity_id,links,project,resources,resources.user",
      });
      setPlans(res.data?.data || []);
    } catch {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [workOrder?.id]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const primaryPlan = useMemo(
    () => pickPrimaryPlanForWorkOrder(plans),
    [plans]
  );

  // All plans for this work order, ordered by when they happen so that an
  // earlier "week 1" plan is listed before a plan scheduled further ahead.
  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => {
      const aDate = a.start_date || "";
      const bDate = b.start_date || "";
      if (aDate && bDate && aDate !== bDate) return aDate < bDate ? -1 : 1;
      if (aDate && !bDate) return -1;
      if (!aDate && bDate) return 1;
      return (a.id || 0) - (b.id || 0);
    });
  }, [plans]);

  const openCreate = () => {
    setSelectedPlan(null);
    setCreateMode(true);
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setSelectedPlan(plan || primaryPlan);
    setCreateMode(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPlan(null);
    setCreateMode(false);
  };

  const handleSaved = (saved) => {
    loadPlans();
    onPlansMutated?.(saved);
  };

  const handleEditProjectEvent = useCallback(() => {
    if (!selectedPlan) return;
    setModalOpen(false);
    setEventForDialog(selectedPlan);
    setEventDialogOpen(true);
  }, [selectedPlan]);

  const planUrl = projectId ? `/dashboard/projects/${projectId}/plan` : null;

  if (!projectId) return null;

  return (
    <>
      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-4 bg-white py-6 px-4 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5 text-teal-600" />
                {t("work_order.plan_modal.section_title")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t("work_order.plan_modal.plans_section_subtitle")}
              </p>
              {sortedPlans.length > 0 ? (
                <p className="mt-1 text-xs font-medium text-slate-400">
                  {t("work_order.plan_modal.plans_count", {
                    count: sortedPlans.length,
                  })}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {planUrl ? (
                <Link
                  to={planUrl}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  {t("work_order.project_plan.open_project_plan")}
                </Link>
              ) : null}
              {canEdit ? (
                <PMButton type="button" onClick={openCreate}>
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  {sortedPlans.length > 0
                    ? t("work_order.plan_modal.add_plan")
                    : t("work_order.plan_modal.schedule_new")}
                </PMButton>
              ) : null}
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500">{t("project.plan.loading")}</p>
          ) : sortedPlans.length === 0 ? (
            <p className="text-sm text-gray-500 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
              {t("work_order.plan_modal.empty_multi")}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {sortedPlans.map((plan, planIndex) => {
                const resources = plan.resources || [];
                const when = formatResourceWhen(resources[0]);
                const planUsers = uniquePlanUsers(resources);
                const dayCount = new Set(
                  resources
                    .map((r) => r.start_time?.substring?.(0, 10))
                    .filter(Boolean)
                ).size;
                return (
                  <li
                    key={plan.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50/80"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">
                        {sortedPlans.length > 1 ? (
                          <span className="mr-2 inline-flex rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">
                            {t("work_order.plan_modal.plan_index", {
                              number: planIndex + 1,
                            })}
                          </span>
                        ) : null}
                        {plan.title || t("work_order.project_plan.untitled")}
                      </p>
                      {plan.description ? (
                        <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                          {stripHtml(plan.description)}
                        </p>
                      ) : null}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {plan.status === "draft" ? (
                          <span className="inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                            {t("work_order.project_plan.badge_draft")}
                          </span>
                        ) : null}
                        {planUsers.length > 0 ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="flex -space-x-1.5">
                              {planUsers.slice(0, 5).map((u, ui) => {
                                const a = planAvatar(u, ui);
                                return (
                                  <span
                                    key={u.id}
                                    title={a.name}
                                    className="inline-grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white ring-2 ring-white"
                                    style={{ background: a.color }}
                                  >
                                    {a.init}
                                  </span>
                                );
                              })}
                              {planUsers.length > 5 ? (
                                <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 ring-2 ring-white">
                                  +{planUsers.length - 5}
                                </span>
                              ) : null}
                            </span>
                            {planUsers.length === 1 ? (
                              <span className="text-slate-600">
                                {planUsers[0].displayTitle ||
                                  planUsers[0].name}
                              </span>
                            ) : (
                              <span className="text-slate-500">
                                {planUsers.length}{" "}
                                {t("work_order.plan_modal.executing_section")}
                              </span>
                            )}
                          </span>
                        ) : null}
                        {dayCount > 1 ? (
                          <span className="text-xs text-slate-500">
                            {dayCount} {t("work_order.plan_modal.days_short")}
                          </span>
                        ) : null}
                        {when ? (
                          <span className="inline-flex items-center gap-1">
                            <ClockIcon className="h-3.5 w-3.5" />
                            {when}
                          </span>
                        ) : plan.status === "draft" ? (
                          <span>{t("work_order.project_plan.status_draft")}</span>
                        ) : null}
                      </div>
                    </div>
                    {canEdit ? (
                      <PMButton
                        type="button"
                        variant="secondary"
                        onClick={() => openEdit(plan)}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        {plan.status === "draft"
                          ? t("project.plan.schedule_plan")
                          : t("edit")}
                      </PMButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <PlanScheduleModal
        open={modalOpen}
        onClose={closeModal}
        workOrder={workOrder}
        project={resolvedProject}
        plan={selectedPlan}
        forceCreate={createMode}
        schedulingMode="work_order"
        onEditProjectEvent={selectedPlan?.id ? handleEditProjectEvent : null}
        onSaved={handleSaved}
      />

      <ProjectEventDialog
        open={eventDialogOpen}
        mode="full"
        onClose={() => {
          setEventDialogOpen(false);
          setEventForDialog(null);
        }}
        event={eventForDialog}
        project={resolvedProject}
        onSaved={() => {
          handleSaved();
          setEventDialogOpen(false);
          setEventForDialog(null);
        }}
      />
    </>
  );
};

/** @deprecated Use WorkOrderPlanSection */
export const WorkOrderProjectPlanSection = WorkOrderPlanSection;
